import { useEffect, useState } from "react";
import { Check, HelpCircle, X } from "lucide-react";
import { registrationAPI, type RsvpResponse } from "@/services/api";

interface RsvpControlProps {
  eventId: string;
  capacity?: number | null;
  attendingCount?: number;
  onChange?: (response: RsvpResponse, attendingCount: number) => void;
}

const OPTIONS: { value: RsvpResponse; label: string; Icon: typeof Check }[] = [
  { value: "going", label: "Going", Icon: Check },
  { value: "maybe", label: "Maybe", Icon: HelpCircle },
  { value: "not_going", label: "Can't go", Icon: X },
];

export default function RsvpControl({
  eventId,
  capacity,
  attendingCount: initialAttending,
  onChange,
}: RsvpControlProps) {
  const [response, setResponse] = useState<RsvpResponse | null>(null);
  const [attending, setAttending] = useState<number>(initialAttending ?? 0);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState<RsvpResponse | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    registrationAPI
      .getMyRsvp(eventId)
      .then((data) => {
        if (!cancelled) setResponse(data.rsvpResponse ?? null);
      })
      .catch(() => {
        /* Not responded yet, or not signed in — the buttons still work. */
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const isFull =
    typeof capacity === "number" && capacity > 0 && attending >= capacity;

  const submit = async (value: RsvpResponse) => {
    if (loading || value === response) return;

    setLoading(true);
    setPending(value);
    setError("");

    try {
      const data = await registrationAPI.rsvpToEvent(eventId, value);
      setResponse(value);
      if (typeof data.attendingCount === "number") {
        setAttending(data.attendingCount);
        onChange?.(value, data.attendingCount);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "Could not save your RSVP. Please try again.");
    } finally {
      setLoading(false);
      setPending(null);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-medium text-secondary">
          {response ? "Your response" : "Are you coming?"}
        </p>
        {typeof capacity === "number" && capacity > 0 && (
          <p className="text-xs text-gray-500 mt-0.5">
            {attending} of {capacity} attending
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {OPTIONS.map(({ value, label, Icon }) => {
          const selected = response === value;
          // Only block newly saying yes; declining is always allowed.
          const blocked = isFull && !selected && value !== "not_going";

          return (
            <button
              key={value}
              type="button"
              onClick={() => submit(value)}
              disabled={loading || blocked}
              aria-pressed={selected}
              className={[
                "inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                selected
                  ? "border-primary bg-primary text-white"
                  : "border-border bg-white text-secondary hover:border-primary hover:text-primary",
                loading || blocked ? "opacity-50 cursor-not-allowed" : "",
              ].join(" ")}
            >
              <Icon className="h-4 w-4" />
              {pending === value ? "Saving..." : label}
            </button>
          );
        })}
      </div>

      {isFull && response !== "going" && response !== "maybe" && (
        <p className="text-xs text-amber-600">
          This event is at capacity. You can still decline, and we'll open more places if someone drops out.
        </p>
      )}

      {response && !error && (
        <p className="text-xs text-gray-500">
          {response === "not_going"
            ? "You've told us you can't make it. You can change this any time."
            : "Saved. You can change your response any time."}
        </p>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
